import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import useAuthStore from '@/stores/authStore'
import websocketService from '@/services/websocket'

// Context providers
import { ResponsiveProvider } from '@/contexts/ResponsiveContext'

// Layout components
import AuthLayout from '@/components/layouts/AuthLayout'
import DashboardLayout from '@/components/layouts/DashboardLayout'

// Auth pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'

// Dashboard pages
import DashboardHome from '@/pages/dashboard/DashboardHome'
import UsersPage from '@/pages/dashboard/UsersPage'
import UserDetailPage from '@/pages/dashboard/UserDetailPage'
import TransactionsPage from '@/pages/dashboard/TransactionsPage'
import WithdrawalsPage from '@/pages/dashboard/WithdrawalsPage'
import StakingPage from '@/pages/dashboard/StakingPage'
import ReferralCodesPage from '@/pages/dashboard/ReferralCodesPage'
import ExportsPage from '@/pages/dashboard/ExportsPage'
import SettingsPage from '@/pages/dashboard/SettingsPage'

// Components
import ProtectedRoute from '@/components/auth/ProtectedRoute'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

function App() {
  const { isAuthenticated, token, initialize, isLoading } = useAuthStore()

  useEffect(() => {
    // Initialize auth store
    const initializeAuth = async () => {
      await initialize()
    }
    initializeAuth()
  }, [initialize])

  useEffect(() => {
    // Connect to WebSocket when authenticated
    if (isAuthenticated && token) {
      websocketService.connect(token).catch(console.error)
    } else {
      websocketService.disconnect()
    }

    // Cleanup on unmount
    return () => {
      websocketService.disconnect()
    }
  }, [isAuthenticated, token])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <ResponsiveProvider>
      <Routes>
        {/* Auth routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
        </Route>

        {/* Dashboard routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="withdrawals" element={<WithdrawalsPage />} />
          <Route path="staking" element={<StakingPage />} />
          <Route path="referral-codes" element={<ReferralCodesPage />} />
          <Route path="exports" element={<ExportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* Redirects */}
        <Route
          path="/"
          element={
            <Navigate
              to={isAuthenticated ? "/dashboard" : "/auth/login"}
              replace
            />
          }
        />
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route path="/register" element={<Navigate to="/auth/register" replace />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ResponsiveProvider>
  )
}

export default App